// frontend/src/components/auth/EmailVerificationForm.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, Loader2, RefreshCw, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/lib/api/authService';

interface EmailVerificationFormProps {
  className?: string;
}

const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({ className = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // const { setUserData } = useAuth(); // Not used in this component

  // Get email and message from state or search params
  const email = location.state?.email || searchParams.get('email') || '';
  const message = location.state?.message || 'Please check your email to verify your account.';
  const isProvider = location.state?.isProvider || false;
  const token = searchParams.get('token');

  // Countdown timer for resend button
  useEffect(() => {
    if (!canResend && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  // Auto-verify if token is provided in URL
  useEffect(() => {
    if (token) {
      handleVerification(token);
    }
  }, [token]);

  const handleVerification = async (verificationToken: string) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await authService.verifyEmail(verificationToken);
      
      if (result.success) {
        setIsSuccess(true);
        // Redirect after success
        setTimeout(() => {
          if (isProvider) {
            navigate('/login', { 
              state: { 
                message: 'Email verified! Your provider application is under review.',
                type: 'success'
              }
            });
          } else {
            navigate('/login', { 
              state: { 
                message: 'Email verified successfully! You can now sign in.',
                type: 'success'
              }
            });
          }
        }, 2000);
      } else {
        setError(result.message || 'Verification failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Email address is required to resend verification.');
      return;
    }

    setIsResendLoading(true);
    setError('');
    setResendSuccess(false);

    try {
      const result = await authService.resendVerificationEmail();
      
      if (result.success) {
        setResendSuccess(true);
        setCanResend(false);
        setCountdown(60);
        // Clear success message after 5 seconds
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setError(result.message || 'Failed to resend verification email. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsResendLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 animate-in zoom-in duration-500">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Email Verified!
            </h2>
            <p className="mt-4 text-gray-600">
              {isProvider 
                ? 'Your email has been verified successfully. Your provider application is now under review.'
                : 'Your email has been verified successfully. You can now access all features.'
              }
            </p>
            
            {isProvider && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <Clock className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <div className="ml-3 text-left">
                    <h3 className="text-sm font-medium text-blue-800">
                      What's Next?
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Our team will review your application</li>
                        <li>Background check will be initiated</li>
                        <li>You'll receive an email within 2-3 business days</li>
                        <li>Once approved, you can start accepting bookings</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-primary-600" />
              <p className="mt-2 text-sm text-gray-500">Redirecting to login page...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <Mail className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {message}
          </p>
          {email && (
            <p className="mt-2 text-sm font-medium text-gray-900">
              Sent to: {email}
            </p>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-600" />
            <p className="mt-2 text-sm text-gray-600">Verifying your email...</p>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 border border-red-200 animate-in slide-in-from-top-2 duration-300">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Resend Success */}
        {resendSuccess && (
          <div className="rounded-md bg-green-50 p-4 border border-green-200 animate-in slide-in-from-top-2 duration-300">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  Verification email sent successfully! Please check your inbox.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <Mail className="h-5 w-5 text-blue-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Check your email
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p className="mb-2">
                  We've sent a verification link to your email address. Click the link to verify your account.
                </p>
                <p className="text-xs">
                  The link will expire in 24 hours for security reasons.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Resend Section */}
        <div className="text-center space-y-4">
          <p className="text-sm text-gray-600">
            Didn't receive the email?
          </p>
          
          <div className="space-y-2">
            <button
              onClick={handleResendVerification}
              disabled={!canResend || isResendLoading || !email}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isResendLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : canResend ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Resend verification email
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  Resend in {countdown}s
                </>
              )}
            </button>
            
            {!email && (
              <p className="text-xs text-red-600">
                Email address is required to resend verification
              </p>
            )}
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            Still not receiving emails?
          </h3>
          <div className="text-sm text-gray-600 space-y-1">
            <p>• Check your spam or junk folder</p>
            <p>• Make sure {email} is correct</p>
            <p>• Add our domain to your safe senders list</p>
            <p>• Try using a different email address</p>
          </div>
          <div className="mt-3">
            <Link
              to="/contact"
              className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
            >
              Contact Support →
            </Link>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col space-y-3">
          <Link
            to="/login"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            Back to Login
          </Link>
          
          <Link
            to="/register"
            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            Try Different Email
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            By verifying your email, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-gray-700">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:text-gray-700">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationForm;